import { useState } from "react";
import { Button, Input, Spinner, TextArea, toast } from "@heroui/react";
import { EvidenceImagePicker } from "./EvidenceImagePicker";
import { useAddToBlacklist } from "./use-blacklist";
import type { EvidenceSlot } from "./types";
import { collectEvidenceKeys, revokePendingPreviews } from "./utils";

const MAX_IMAGES = 9;

export type AddBlacklistUserFormProps = {
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  onSuccess: () => void;
  onCancel?: () => void;
  isDisabled?: boolean;
};

export function AddBlacklistUserForm({
  uploadFile,
  onSuccess,
  onCancel,
  isDisabled = false,
}: AddBlacklistUserFormProps) {
  const [uid, setUid] = useState("");
  const [reason, setReason] = useState("");
  const [slots, setSlots] = useState<EvidenceSlot[]>([]);
  // 成功 / 失败 toast 与黑名单缓存失效都在 mutation 里统一处理
  const addToBlacklist = useAddToBlacklist();
  const loading = addToBlacklist.isPending;

  const reset = () => {
    revokePendingPreviews(slots);
    setUid("");
    setReason("");
    setSlots([]);
  };

  const handleSubmit = () => {
    const trimmed = uid.trim();
    if (!trimmed) {
      toast.danger("请输入用户 UID");
      return;
    }
    if (slots.some((s) => s.kind === "pending" && s.status === "uploading")) {
      toast.danger("请等待图片上传完成");
      return;
    }
    if (slots.some((s) => s.kind === "pending" && s.status === "error")) {
      toast.danger("请先删除上传失败的图片");
      return;
    }
    const imageKeys = collectEvidenceKeys(slots);

    addToBlacklist.mutate(
      {
        uid: trimmed,
        reason: reason.trim() || undefined,
        images: imageKeys.length ? imageKeys : undefined,
      },
      {
        onSuccess: () => {
          reset();
          onSuccess();
        },
      },
    );
  };

  const uploading = slots.some((s) => s.kind === "pending" && s.status === "uploading");

  return (
    <div className="flex flex-col gap-4">
      <Input
        placeholder="输入用户 UID"
        value={uid}
        onChange={(e) => setUid(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void handleSubmit()}
        disabled={isDisabled || loading}
      />
      <TextArea
        placeholder="输入加入黑名单的理由（可选）"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        disabled={isDisabled || loading}
      />
      <div className="flex flex-col gap-2">
        <span className="text-sm text-gray-600">
          举证图片（可选，最多 {MAX_IMAGES} 张，单张不超过 10MB）
        </span>
        <EvidenceImagePicker
          slots={slots}
          onSlotsChange={setSlots}
          uploadFile={uploadFile}
          maxCount={MAX_IMAGES}
          disabled={isDisabled || loading}
        />
      </div>
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button
            variant="tertiary"
            onPress={() => {
              reset();
              onCancel();
            }}
            isDisabled={loading}
          >
            取消
          </Button>
        ) : null}
        <Button
          variant="primary"
          onPress={handleSubmit}
          isPending={loading}
          isDisabled={isDisabled || uploading}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              添加
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
